import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getApiUrl } from '../config';
import { apiRequest, isTokenExpired } from '../utils/apiUtils';
import { identify, reset } from '../lib/analytics';

export interface User {
  id: string;
  email: string;
  name: string;
  nickname?: string;
  position?: string;
  rating: number;
  avatar?: string;
  googleId?: string;
  role?: 'PLAYER' | 'VENUE_OWNER' | 'BOTH' | 'ADMIN';
  isAdmin?: boolean;
  emailVerified?: boolean;
  // Lo devuelve /api/auth/me. Se usa como `signup_at` en las person properties
  // de PostHog, que es lo que permite medir time-to-activation por cohorte.
  createdAt?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loginWithGoogle: () => void;
  logout: () => void;
  loading: boolean;
  setToken: (token: string | null) => void;
  setUser: (user: User | null) => void;
  selectRole: (role: 'PLAYER' | 'VENUE_OWNER' | 'BOTH') => Promise<void>;
  checkNeedsRoleSelection: () => boolean;
}

// interface RegisterData {
//   email: string;
//   password: string;
//   name: string;
//   nickname?: string;

//   position?: string;
//   rating?: number;
// }

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Control global para evitar múltiples inicializaciones
let globalAuthInitialized = false;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setTokenState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchingUser, setFetchingUser] = useState(false);
  const [initialized, setInitialized] = useState(false);




  const fetchUserProfile = async (authToken: string) => {
    if (fetchingUser) {
      return;
    }
    
    // Verificar si el token está expirado antes de hacer la petición
    if (isTokenExpired(authToken)) {
      // Token expirado detectado, limpiando sesión...
      localStorage.removeItem('authToken');
      setTokenState(null);
      setUser(null);
      setLoading(false);
      return;
    }
    
    try {
      setFetchingUser(true);
      
      const result = await apiRequest('/api/auth/me', { requireAuth: true });
      
      if (result.data) {
        setUser(result.data);
        identify(
          result.data.id,
          {
            role: result.data.role || 'PLAYER',
            position: result.data.position || null,
            skill_level: result.data.rating ?? null,
            email_verified: !!result.data.emailVerified,
            last_platform: 'web',
          },
          { signup_at: result.data.createdAt || undefined }
        );
      } else {
        // La utilidad apiRequest ya maneja la limpieza de tokens expirados
        if (result.code === 'TOKEN_EXPIRED' || result.code === 'MALFORMED_TOKEN') {
          setTokenState(null);
          setUser(null);
        }
      }
    } catch (error) {
      // Handle error appropriately instead of console.error
      localStorage.removeItem('authToken');
      setTokenState(null);
      setUser(null);
    } finally {
      setLoading(false);
      setFetchingUser(false);
    }
  };

  useEffect(() => {
    if (globalAuthInitialized) return; // Evitar ejecución múltiple en StrictMode

    globalAuthInitialized = true;
    setInitialized(true);

    const initAuth = async () => {
      const savedToken = localStorage.getItem('authToken');

      // Flag para habilitar/deshabilitar bypass de login en desarrollo
      const ENABLE_DEV_BYPASS =
        process.env.REACT_APP_ENABLE_DEV_BYPASS === 'true' &&
        process.env.NODE_ENV === 'development';
      const DEV_EMAIL = process.env.REACT_APP_DEV_EMAIL;

      if (savedToken) {
        // En desarrollo, si el bypass está habilitado, limpiar token viejo y usar bypass
        if (ENABLE_DEV_BYPASS && DEV_EMAIL) {
          localStorage.removeItem('authToken');
          // eslint-disable-next-line no-console
          console.log('🔧 Dev bypass habilitado, limpiando token viejo');
          // Continuar con el bypass de login
        } else {
          setTokenState(savedToken);
          await fetchUserProfile(savedToken);
          return;
        }
      }

      // Bypass de login en desarrollo
      if (ENABLE_DEV_BYPASS && DEV_EMAIL) {
        // eslint-disable-next-line no-console
        console.log('🔧 Dev bypass habilitado', { email: DEV_EMAIL });

        try {
          const devLoginUrl = `${getApiUrl('/api/auth/dev-login')}?email=${encodeURIComponent(DEV_EMAIL)}`;
          // eslint-disable-next-line no-console
          console.log('📡 Intentando dev-login:', devLoginUrl);

          const response = await fetch(devLoginUrl);

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          if (data && data.token) {
            // eslint-disable-next-line no-console
            console.log('✅ Token recibido de dev-login');
            localStorage.setItem('authToken', data.token);
            setTokenState(data.token);
            await fetchUserProfile(data.token);
            return;
          } else {
            // eslint-disable-next-line no-console
            console.error('❌ No se recibió token del dev-login');
            setLoading(false);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('❌ Error en login de desarrollo:', error);
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginWithGoogle = () => {
    try {
      
      // window.location.href no respeta el proxy, necesitamos la URL completa
      let googleAuthUrl;
      if (process.env.NODE_ENV === 'development') {
        googleAuthUrl = 'http://localhost:5001/api/auth/google';
      } else {
        // En producción, usar la URL completa
        googleAuthUrl = getApiUrl('/api/auth/google');
      }
      
      
      // Verificar que la URL sea válida
      if (!googleAuthUrl || !googleAuthUrl.includes('/api/auth/google')) {
        // eslint-disable-next-line no-console
        console.error('❌ URL de autenticación inválida:', googleAuthUrl);
        throw new Error('URL de autenticación inválida');
      }
      
      // Redirigir a Google OAuth
      window.location.href = googleAuthUrl;
      
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Error en loginWithGoogle:', error);
      // Mostrar error al usuario
      alert('Error al iniciar sesión con Google. Por favor, intenta nuevamente.');
    }
  };

  const logout = () => {
    setTokenState(null);
    setUser(null);
    localStorage.removeItem('authToken');
    reset();
  };

  const selectRole = async (role: 'PLAYER' | 'VENUE_OWNER' | 'BOTH'): Promise<void> => {
    if (!token) {
      throw new Error('No token available');
    }

    const response = await fetch(getApiUrl('/api/auth/select-role'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ role })
    });

    if (response.ok) {
      const updatedUser = await response.json();
      setUser(updatedUser);
    } else {
      const error = await response.json();
      throw new Error(error.message || 'Failed to select role');
    }
  };

  const checkNeedsRoleSelection = (): boolean => {
    return user !== null && !user.role;
  };

  // const forgotPassword = async (email: string) => {
  //   try {
  //     const response = await fetch(getApiUrl('/api/auth/forgot-password'), {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify({ email }),
  //     });

  //     const data = await response.json();

  //     if (response.ok) {
  //       return { success: true, resetUrl: data.resetUrl };
  //     } else {
  //       return { success: false, error: data.error };
  //     }
  //   } catch (error) {
  //     console.error('Error during forgot password:', error);
  //     return { success: false, error: 'Error de conexión' };
  //   }
  // };

  // const resetPassword = async (token: string, newPassword: string) => {
  //   try {
  //     const response = await fetch(getApiUrl('/api/auth/reset-password'), {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify({ token, newPassword }),
  //     });

  //     const data = await response.json();

  //     if (response.ok) {
  //       return { success: true };
  //     } else {
  //       return { success: false, error: data.error };
  //     }
  //   } catch (error) {
  //     console.error('Error during reset password:', error);
  //     return { success: false, error: 'Error de conexión' };
  //   }
  // };

  // const verifyEmail = async () => {
  //   try {
  //     const response = await fetch(getApiUrl('/api/auth/verify-email'), {
  //       method: 'POST',
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'application/json',
  //       },
  //     });

  //     const data = await response.json();

  //     if (response.ok) {
  //       return { success: true, code: data.code };
  //       } else {
  //       return { success: false, error: data.error };
  //     }
  //   } catch (error) {
  //     console.error('Error during email verification:', error);
  //     return { success: false, error: 'Error de conexión' };
  //   }
  // };

  const value: AuthContextType = {
    user,
    token,
    loginWithGoogle,
    logout,
    loading,
    setToken: setTokenState,
    setUser,
    selectRole,
    checkNeedsRoleSelection,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 